import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { PortfolioBrand } from "@/hooks/use-portfolio";

function CountTile({
  label,
  count,
  href,
  destructive,
}: {
  label: string;
  count: number;
  href: string;
  destructive?: boolean;
}) {
  return (
    <Link to={href} className="rounded-md border bg-background p-2 hover:bg-accent transition-colors">
      <div className={cn("text-lg font-semibold leading-tight", destructive && "text-destructive")}>
        {count}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </Link>
  );
}

function FlagPill({
  label,
  enabled,
  pausedAt,
  pauseReason,
}: {
  label: string;
  enabled: boolean;
  pausedAt?: string | null;
  pauseReason?: string | null;
}) {
  const { t } = useTranslation("dashboard");
  const paused = !!pausedAt;
  const stateLabel = paused ? t("flag.paused") : enabled ? t("flag.on") : t("flag.off");
  const pauseReasonLabel = pauseReason ? t(`pause.${pauseReason}`) : pauseReason;
  return (
    <span
      title={
        paused && pauseReason ? t("portfolioCard.pausedTitle", { reason: pauseReasonLabel }) : undefined
      }
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        paused
          ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-400"
          : enabled
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
            : "bg-muted text-muted-foreground",
      )}
    >
      {label} · {stateLabel}
    </span>
  );
}

function BudgetBar({
  label,
  spentUsd,
  capUsd,
  percentUsed,
  href,
}: {
  label: string;
  spentUsd: string;
  capUsd: string | null;
  percentUsed: number;
  href: string;
}) {
  const { t } = useTranslation("dashboard");
  const noCap = t("budgetBar.noCap");
  const over = percentUsed >= 1;
  const warn = percentUsed >= 0.8;
  return (
    <Link to={href} className="block space-y-1 group">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground group-hover:text-foreground transition-colors">
          {label}
        </span>
        <span className={cn(over ? "text-destructive" : warn ? "text-amber-600" : "text-muted-foreground")}>
          {capUsd === null ? `$${spentUsd} · ${noCap}` : `$${spentUsd} / $${capUsd}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full",
            over ? "bg-destructive" : warn ? "bg-amber-500" : "bg-primary",
          )}
          style={{ width: `${Math.min(percentUsed, 1) * 100}%` }}
        />
      </div>
    </Link>
  );
}

export function PortfolioCard({ brand }: { brand: PortfolioBrand }) {
  const { t } = useTranslation("dashboard");
  const { slug, automation } = brand;
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Header — whole header links to the brand */}
      <Link to={`/${slug}`} className="flex items-center gap-2 group">
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt="" className="h-7 w-7 rounded-md object-cover flex-shrink-0" />
        ) : (
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-brand-subtle text-[11px] font-semibold text-brand-subtle-text">
            {brand.name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="truncate font-semibold group-hover:underline">{brand.name}</span>
      </Link>

      {/* Count tiles */}
      <div className="grid grid-cols-3 gap-2">
        <CountTile label={t("portfolioCard.pendingReview")} count={brand.pendingReview} href={`/${slug}/review`} />
        <CountTile label={t("portfolioCard.scheduledNext7d")} count={brand.scheduledNext7d} href={`/${slug}/calendar`} />
        <CountTile
          label={t("portfolioCard.failedPosts7d")}
          count={brand.failedPosts7d}
          href={`/${slug}/calendar`}
          destructive={brand.failedPosts7d > 0}
        />
      </div>

      {/* Automation flag pills */}
      <div className="flex flex-wrap gap-1.5">
        <FlagPill
          label={t("portfolioCard.autopilot")}
          enabled={automation.autopilot.enabled}
          pausedAt={automation.autopilot.pausedAt}
          pauseReason={automation.autopilot.pauseReason}
        />
        <FlagPill
          label={t("portfolioCard.ads")}
          enabled={automation.ads.enabled}
          pausedAt={automation.ads.pausedAt}
          pauseReason={automation.ads.pauseReason}
        />
        <FlagPill label={t("portfolioCard.autoReply")} enabled={automation.inboxAutoReply.enabled} />
        <FlagPill label={t("portfolioCard.aiReview")} enabled={automation.aiReview.enabled} />
      </div>

      {/* Budget bars */}
      <div className="space-y-2">
        <BudgetBar
          label={t("portfolioCard.aiSpend")}
          spentUsd={brand.aiSpend.spentUsd}
          capUsd={brand.aiSpend.capUsd}
          percentUsed={brand.aiSpend.percentUsed}
          href={`/${slug}/settings/ai-usage`}
        />
        <BudgetBar
          label={t("portfolioCard.adSpend")}
          spentUsd={brand.adSpend.committedUsd}
          capUsd={brand.adSpend.capUsd}
          percentUsed={brand.adSpend.percentUsed}
          href={`/${slug}/ads`}
        />
      </div>

      {/* Latest insight footer */}
      {brand.latestInsight && (
        <Link
          to={`/${slug}/analytics`}
          className="block border-t pt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <span className="line-clamp-2">{brand.latestInsight.summary}</span>
        </Link>
      )}
    </div>
  );
}
